import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import BaseSelector from '@src/components/BaseSelector';
import type { CostCenter } from '@src/models/interfaces/costCenter.interface';
import {
  COST_CENTER_UNASSIGNED_FILTER_VALUE,
} from '@src/packages/costCenters/constants';
import { flattenActiveForSelector } from '@src/packages/costCenters/utils/buildCostCenterTree';
import {
  selectedClientIncludesBranches,
} from '@src/packages/costCenters/utils/expandCostCenterIds';
import CostCenterService from '@src/services/CostCenterService';
import { REMOTE_CONFIG_KEYS } from '@src/packages/utils/hooks/remoteConfigs/remoteConfigKeys';
import { useRemoteConfigAccess } from '@src/packages/utils/hooks/remoteConfigs/useRemoteConfigAccess';

export interface CostCenterFilterSelectorProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
  /** When false, hide the control entirely (caller already gated). Default: remote-config gate. */
  forceVisible?: boolean;
  placeholderId?: string;
}

type SelectorItem = CostCenter & { label: string };

/**
 * Multi-select cost-center filter for reports/dashboards.
 * Selecting a client includes its branches at query time; the label hints that.
 * Includes a synthetic “Unassigned” option for records stamped with null CC.
 *
 * Options come from `CostCenterService.list` (API), which already scopes
 * responsible users to their accessible cost centers server-side.
 */
const CostCenterFilterSelector: React.FC<CostCenterFilterSelectorProps> = ({
  value = [],
  onChange,
  disabled,
  className,
  forceVisible,
  placeholderId = 'costCenters.filter.selector.placeholder',
}) => {
  const intl = useIntl();
  const { isAllowed: costCentersAllowed } = useRemoteConfigAccess(REMOTE_CONFIG_KEYS.COST_CENTERS);
  const organizationId = useSelector(
    ({ organizations }: { organizations: { organization: { id?: string } } }) =>
      organizations.organization?.id,
  );
  const serviceRef = useRef<CostCenterService | null>(null);
  if (serviceRef.current === null) {
    serviceRef.current = new CostCenterService();
  }
  const [items, setItems] = useState<SelectorItem[]>([]);
  const [rawList, setRawList] = useState<CostCenter[]>([]);
  const [loading, setLoading] = useState(false);

  const visible = forceVisible === true || (forceVisible !== false && costCentersAllowed);

  useEffect(() => {
    if (!visible || !organizationId) {
      setItems([]);
      setRawList([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    serviceRef.current!
      .list(organizationId, true)
      .then(list => {
        if (cancelled) return;
        setRawList(list);
        const flattened = flattenActiveForSelector(list).map(item => {
          if (!item.parentId) {
            const hasBranches = list.some(cc => cc.parentId === item.id && cc.active);
            return {
              ...item,
              label: hasBranches
                ? intl.formatMessage(
                    { id: 'costCenters.filter.selector.clientWithBranches' },
                    { label: item.label },
                  )
                : item.label,
            };
          }
          return item;
        });
        const unassigned: SelectorItem = {
          id: COST_CENTER_UNASSIGNED_FILTER_VALUE,
          organizationId,
          parentId: null,
          code: '',
          name: intl.formatMessage({ id: 'costCenters.filter.unassigned' }),
          responsibleUserIds: [],
          active: true,
          label: intl.formatMessage({ id: 'costCenters.filter.unassigned' }),
        };
        setItems([unassigned, ...flattened]);
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setRawList([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [intl, organizationId, visible]);

  const handleChange = useMemo(
    () => (next: string[] | undefined) => {
      onChange?.(Array.isArray(next) ? next.filter(Boolean) : []);
    },
    [onChange],
  );

  const hint = selectedClientIncludesBranches(value, rawList)
    ? intl.formatMessage({ id: 'costCenters.filter.selector.clientIncludesBranchesHint' })
    : null;

  if (!visible) {
    return null;
  }

  return (
    <div className={className}>
      <BaseSelector<SelectorItem>
        items={items}
        loading={loading}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        placeholderId={placeholderId}
        showAllOption={false}
        mode="multiple"
        maxTagCount={2}
        getItemId={item => item.id}
        getItemName={item => item.label}
        {...({ allowClear: true } as Record<string, unknown>)}
      />
      {hint ? <div className="gx-text-muted gx-fs-sm gx-mt-1">{hint}</div> : null}
    </div>
  );
};

export default React.memo(CostCenterFilterSelector);
